import '@fontsource/open-sans';

import { alpha, createTheme } from '@mui/material/styles';

// shadcn/ui zinc scale — shared naming for light/dark token selection
const zinc = {
  50: '#fafafa',
  100: '#f4f4f5',
  200: '#e4e4e7',
  300: '#d4d4d8',
  400: '#a1a1aa',
  500: '#71717a',
  600: '#52525b',
  900: '#18181b',
};

// Light-mode greys: between legacy (#868686 / #E0E0E0) and zinc-600 (too heavy).
const lightGray = {
  secondary: '#6d6d6d',
  disabled: '#989898',
  divider: '#e0e0e0',
  border: '#cfcfcf',
  subtle: '#f6f6f6',
};

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary:
      mode === 'light'
        ? { main: '#000000', light: '#333333', dark: '#000000', contrastText: '#FFFFFF' }
        : { main: '#e4e4e7', light: '#fafafa', dark: '#a1a1aa', contrastText: '#18181b' },
    secondary: {
      main: '#E10050',
      light: '#ED6696',
      dark: '#A0003A',
      contrastText: '#FFFFFF',
    },
    info:
      mode === 'light'
        ? { main: '#2196F3', light: '#64B5F6', dark: '#1976D2', contrastText: '#FFFFFF' }
        : { main: '#90CAF9', light: '#E3F2FD', dark: '#42A5F5', contrastText: '#09090b' },
    error: {
      light: '#E57373',
      main: '#F44336',
      dark: '#D32F2F',
      contrastText: '#FFFFFF',
    },
    warning: {
      light: '#FFB74D',
      main: '#FF9800',
      dark: '#F57C00',
      contrastText: '#000000',
    },
    success: {
      light: '#81C784',
      main: '#4CAF50',
      dark: '#388E3C',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: mode === 'light' ? '#000000' : '#fafafa',
      secondary: mode === 'light' ? lightGray.secondary : zinc[400],
      disabled: mode === 'light' ? lightGray.disabled : zinc[500],
    },
    background: {
      default: mode === 'light' ? '#FFFFFF' : '#1a1a1e',
      paper: mode === 'light' ? '#FFFFFF' : '#232327',
      subtle: mode === 'light' ? lightGray.subtle : '#252528',
    },
    divider: mode === 'light' ? lightGray.divider : '#27272a',
    action: {
      hover: mode === 'light' ? lightGray.subtle : '#27272a',
      selected: mode === 'light' ? '#eeeeee' : '#3f3f46',
      disabled: mode === 'light' ? lightGray.disabled : '#52525b',
    },
    custom: {
      subtle: mode === 'light' ? lightGray.subtle : '#252528',
      border: mode === 'light' ? lightGray.border : '#27272a',
      loopLink: '#9c27b0',
    },
  },
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2em',
      color: mode === 'light' ? lightGray.secondary : zinc[400],
      lineHeight: '60px',
    },
    h2: {
      fontSize: '1.5em',
      color: mode === 'light' ? lightGray.secondary : zinc[400],
      lineHeight: '44px',
    },
    h3: {
      fontSize: '1.2em',
      fontWeight: 300,
      color: mode === 'light' ? lightGray.secondary : zinc[400],
      lineHeight: '33px',
    },
    h4: {
      fontSize: '1.125em',
      color: mode === 'light' ? '#000000' : '#fafafa',
      lineHeight: '16px',
    },
    body1: {
      fontSize: 14,
      lineHeight: '19px',
    },
    body2: {
      fontSize: 14,
      lineHeight: '19px',
      fontWeight: 300,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    // Button contract (prefer plain <Button> over local styled wrappers):
    //   contained + primary  → ink CTA
    //   outlined + primary   → ghost / secondary
    //   contained + success  → start / run
    //   contained + warning  → test run
    //   contained + error    → destructive filled
    //   outlined + error     → destructive ghost
    //   size="small"         → node/dialog density
    //   MuiFab               → canvas side-menu icons
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        sizeSmall: {
          fontSize: '13px',
          fontWeight: 500,
          letterSpacing: 0,
          padding: '4px 12px',
          minHeight: 28,
          borderRadius: 4,
          // Beat `contained` tracking so dense filled CTAs stay compact.
          '&.MuiButton-contained': {
            letterSpacing: 0,
          },
        },
        sizeMedium: {
          fontSize: '14px',
          fontWeight: 400,
        },
        // Tracking only on medium+ filled CTAs (not ghost/outlined).
        contained: {
          letterSpacing: '1.25px',
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'light'
                ? theme.palette.text.secondary
                : theme.palette.primary.dark,
            boxShadow: 'none',
          },
        }),
        containedSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.success.dark,
            boxShadow: 'none',
          },
        }),
        containedError: ({ theme }) => ({
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
            boxShadow: 'none',
          },
        }),
        containedWarning: ({ theme }) => ({
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.warning.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.warning.dark,
            boxShadow: 'none',
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.text.disabled,
            color: theme.palette.text.primary,
          },
        }),
        // Legacy alias: color="info" outlined used to mean ghost cancel.
        // Prefer variant="outlined" (primary). Kept so old call sites stay ghost, not blue.
        outlinedInfo: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.text.disabled,
            color: theme.palette.text.primary,
          },
        }),
        outlinedError: ({ theme }) => ({
          color: theme.palette.error.main,
          borderColor: theme.palette.divider,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            borderColor: theme.palette.error.main,
          },
        }),
        textPrimary: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    // Square icon FABs (canvas side menu). Prefer theme tokens over per-call fabStyles.
    MuiFab: {
      defaultProps: {
        color: 'default',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: 'none',
          borderRadius: 4,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-disabled': {
            '& input.Mui-disabled, & textarea.Mui-disabled': {
              color: theme.palette.text.primary,
              WebkitTextFillColor: theme.palette.text.primary,
              opacity: 0.8,
            },
          },
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 1000,
      },
    },
  },
});

export const createAppTheme = (mode) => createTheme(getDesignTokens(mode));

// Default export for backward compatibility during migration
const theme = createAppTheme('light');
export default theme;
